import {
    BodyRegular,
    BodySmall,
    Card,
    FlexCol,
    FlexColMd,
    FlexMd,
    FlexSm,
    FlexSpaceBetween,
    Headline6,
} from 'components/Elements';
import React from 'react';
import { breakpoints, sizes, theme } from 'theme';
import strings from '../../../../strings/strings.json';
import { ActivityType, GenerateErrorReason, GenerateType } from 'models/Activity';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import ValidateIcon from 'assets/icons/indicators/ValidateIcon';
import PreviewIcon from 'assets/icons/controls/PreviewIcon';
import RefreshIcon from 'assets/icons/RefreshIcon';
import { PercentageProgressBar } from 'components/progressBar/ProgressBar';
import ActivityTypeLabel from '../ActivityTypeLabel';
import { ModuleItem } from './ModuleItem';
import { styled } from 'styled-components';
import { IconSize } from 'assets/icons/icon-sizes';
import useModifyRequest from 'hooks/useModifyRequest';
import { GenerateItem } from './TaskGenerateItem';
import { GenerateButton } from './GenerateButton';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import Button from 'components/buttons/Button';

type ModuleItemProps = {
    module: ModuleItem;
    setTaskIdToReview: React.Dispatch<React.SetStateAction<string>>;
};

export function ModuleListItem({ module, setTaskIdToReview }: ModuleItemProps): JSX.Element {
    return (
        <ModuleCard>
            <Headline6>{`${strings.courseAIGeneratedContentComponent.moduleTitlePrefix} ${module.moduleOrder}: ${module.moduleTitle}`}</Headline6>
            <ActivityGrid>
                {module.generateItems.map((content: GenerateItem, taskIndex) => {
                    return (
                        <ActivityCardComponent
                            key={taskIndex}
                            generateItem={content}
                            setTaskIdToReview={setTaskIdToReview}
                        />
                    );
                })}
            </ActivityGrid>
        </ModuleCard>
    );
}

type ActivityCardComponentProps = {
    generateItem: GenerateItem;
    setTaskIdToReview: React.Dispatch<React.SetStateAction<string>>;
};

function ActivityCardComponent({ generateItem, setTaskIdToReview }: ActivityCardComponentProps): JSX.Element {
    const context = React.useContext(BuildCourseContext);

    const { modifyData: generateContent, loading: generatingContent } = useModifyRequest(
        `generate-content-statuses/${generateItem.id}/retry`,
        'POST',
    );

    async function startGenerateClick() {
        await generateContent({});
        await context.refetchCourse();
    }

    let approvedLabel = <></>;
    let buttonRow = <></>;

    const status = generateItem.status;

    switch (status) {
        case 'APPROVED':
            approvedLabel = (
                <FlexSm>
                    <BodySmall $bold $nowrap>
                        {strings.courseAIGeneratedContentComponent.approvedStatus}
                    </BodySmall>
                    <ValidateIcon />
                </FlexSm>
            );
            buttonRow = (
                <Button
                    title={strings.courseAIGeneratedContentComponent.viewButton.title}
                    aria={strings.courseAIGeneratedContentComponent.viewButton.aria}
                    icon={<PreviewIcon size={IconSize.MEDIUM} />}
                    fullwidth
                    onClick={() => {
                        context.setActiveStep('courseStructureAndContent');
                        context.setEditActivityId(generateItem.activityId);
                    }}
                />
            );
            break;
        case 'GENERATED':
            let reviewButtonTitle = '';
            let reviewButtonAria = '';

            switch (generateItem.generateType) {
                default:
                case GenerateType.AUDIO_TO_TEXT:
                case GenerateType.VIDEO_TO_TEXT:
                    reviewButtonTitle = strings.courseAIGeneratedContentComponent.reviewTranscriptButton.title;
                    reviewButtonAria = strings.courseAIGeneratedContentComponent.reviewTranscriptButton.aria;
                    break;
                case GenerateType.TEXT_TO_AUDIO:
                    reviewButtonTitle = strings.courseAIGeneratedContentComponent.reviewAudioButton.title;
                    reviewButtonAria = strings.courseAIGeneratedContentComponent.reviewAudioButton.aria;
                    break;
                case GenerateType.MODULE_QUESTIONNAIRE:
                    reviewButtonTitle = strings.courseAIGeneratedContentComponent.reviewQuestionnaireButton.title;
                    reviewButtonAria = strings.courseAIGeneratedContentComponent.reviewQuestionnaireButton.aria;
                    break;
            }

            buttonRow = (
                <Button
                    title={reviewButtonTitle}
                    aria={reviewButtonAria}
                    onClick={() => setTaskIdToReview(generateItem.id)}
                    icon={<PreviewIcon size={IconSize.MEDIUM} colour={theme.cardBackground} />}
                    fullwidth
                    colour='PRIMARY'
                />
            );
            break;
        case 'ERRORED':
            buttonRow = (
                <FlexColMd>
                    <FlexSpaceBetween>
                        <ErrorNotifierSlim description={getFriendlyError(generateItem.error)} />
                    </FlexSpaceBetween>
                    <ButtonsContainer>
                        <Button
                            title={strings.courseAIGeneratedContentComponent.retryButton.title}
                            aria={strings.courseAIGeneratedContentComponent.retryButton.aria}
                            onClick={async () => await startGenerateClick()}
                            submitting={generatingContent}
                            icon={<RefreshIcon />}
                            fullwidth
                        />
                        <FlexSpaceBetween>{strings.courseAIGeneratedContentComponent.retryError}</FlexSpaceBetween>
                    </ButtonsContainer>
                </FlexColMd>
            );
            break;
        case 'STARTED':
            let expectedMinsToComplete = 2;
            let note = '';

            switch (generateItem.generateType) {
                case GenerateType.TEXT_TO_AUDIO:
                    expectedMinsToComplete = 1;
                    note = strings.courseAIGeneratedContentComponent.generatingNote.audio;
                    break;
                case GenerateType.AUDIO_TO_TEXT:
                case GenerateType.VIDEO_TO_TEXT:
                    expectedMinsToComplete = 2;
                    note = strings.courseAIGeneratedContentComponent.generatingNote.transcript;
                    break;
                default:
                    break;
            }

            buttonRow = (
                <EstimatedTimeBase>
                    <BodySmall>{note}</BodySmall>
                    <PercentageProgressBar
                        label={`${strings.courseAIGeneratedContentComponent.estimatedTime}: ${expectedMinsToComplete} ${strings.courseAIGeneratedContentComponent.min}`}
                        expectedMinsToComplete={expectedMinsToComplete}
                        dateStart={generateItem.dateGenerateStart!}
                    />
                </EstimatedTimeBase>
            );
            break;
        case 'NOT_STARTED':
        default:
            buttonRow = (
                <GenerateButton
                    generatedType={generateItem.generateType}
                    onClick={async () => await startGenerateClick()}
                    disableButton={generatingContent}
                />
            );
            break;
    }

    return (
        <ActivityCard>
            <FlexColMd>
                <ActivityTitle $bold>{generateItem.activityTitle}</ActivityTitle>
                <TypeAndApprovedRow>
                    <ActivityTypeLabel
                        activityType={generateItem.activityType as ActivityType}
                        buildMethod={generateItem.taskBuildMethod!}
                    />
                    {approvedLabel}
                </TypeAndApprovedRow>
            </FlexColMd>
            {buttonRow}
        </ActivityCard>
    );
}

const TypeAndApprovedRow = styled(FlexMd)`
    justify-content: space-between;
    align-items: center;
`;

const ActivityTitle = styled(BodyRegular)`
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ModuleCard = styled(Card)`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
`;

const ActivityCard = styled(Card)`
    flex: 0 0 calc(25% - ${sizes.spacingSm});
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: ${sizes.spacingMd};
    min-width: 360px;
    max-width: 360px;
    height: 200px;

    @media (max-width: ${breakpoints.lg}) {
        flex: 0 0 calc(100% - ${sizes.spacingSm});
        min-width: unset;
        max-width: unset;
    }
`;

const ActivityGrid = styled(FlexSm)`
    flex-wrap: wrap;
`;

const EstimatedTimeBase = styled(FlexCol)`
    width: 100%;
`;

const ButtonsContainer = styled(FlexColMd)`
    justify-content: space-between;
    flex: 45%;

    @media (max-width: ${breakpoints.md}) {
        flex-direction: column;
        width: 100%;
    }
`;

function getFriendlyError(error: GenerateErrorReason): string {
    let friendlyError = '';

    switch (error) {
        case GenerateErrorReason.MISSING_ENV_VAR_POLLY_REGION:
        case GenerateErrorReason.MISSING_ENV_VAR_REGION:
        case GenerateErrorReason.MISSING_ENV_VAR_DOCUMENTS_REGION:
        case GenerateErrorReason.MISSING_ENV_VAR_DOCUMENTS_BUCKET_NAME:
            friendlyError = strings.courseAIGeneratedContentComponent.generateContentErrors.serverError;
            break;
        case GenerateErrorReason.ACTIVITY_NOT_TASK:
            friendlyError = strings.courseAIGeneratedContentComponent.generateContentErrors.activityNotTask;
            break;
        case GenerateErrorReason.TASK_NO_CONTENT:
            friendlyError = strings.courseAIGeneratedContentComponent.generateContentErrors.taskNoContent;
            break;
        case GenerateErrorReason.CONTENT_ALREADY_GENERATED:
            friendlyError = strings.courseAIGeneratedContentComponent.generateContentErrors.contentAlreadyGenerated;
            break;
        case GenerateErrorReason.POLLY_ERROR:
        case GenerateErrorReason.POLLY_RESPONSE_NO_AUDIO:
            friendlyError = strings.courseAIGeneratedContentComponent.generateContentErrors.pollyError;
            break;
        case GenerateErrorReason.UPLOAD_AUDIO_ERROR:
            friendlyError = strings.courseAIGeneratedContentComponent.generateContentErrors.uploadAudioError;
            break;
        case GenerateErrorReason.UPDATE_STATUS_ERROR:
            friendlyError = strings.courseAIGeneratedContentComponent.generateContentErrors.updateStatusError;
            break;
        case GenerateErrorReason.NONE:
        default:
            return '';
    }

    return `${strings.courseAIGeneratedContentComponent.generateContentErrors.prefix} ${friendlyError}`;
}
